/**
 * Per-account storage namespacing — the single owner of key construction for
 * every browser key that holds ACCOUNT-OWNED data.
 *
 *   apiboo-<userId>-local-snapshot-<workspaceId>
 *   apiboo-<userId>-local-ws-manifest
 *   apiboo-<userId>-cloud-schemas-<workspaceId>
 *   api-client-<userId>-collections | history | environments | workspaces | ...
 *
 * Signed out, the namespace is `_local`. The invariant is structural, not
 * defensive: a key belonging to another account is UNREACHABLE, because the
 * key name needed to read it is never constructed. Nothing filters, nothing
 * checks a role — there is simply no path from account A's session to account
 * B's key names.
 *
 * DEVICE-GLOBAL keys are never namespaced and never torn down (see
 * DEVICE_GLOBAL_KEYS / DEVICE_GLOBAL_PREFIXES): theme + window widths
 * (`apiboo-app-preferences`), the device client id, first-run consent, the
 * region cache, the update manifest, feature flags, the device account
 * registry, the extension CODE inventory (`apiboo-extensions-installed`) and
 * the proxy TRANSPORT (`apiboo-proxy-transport`). Those describe the DEVICE,
 * not the person signed into it; clearing them on logout would be a regression
 * (theme resets, re-consent, extensions vanishing).
 *
 * The line between the two lists is ownership, not convenience: anything that
 * is a CREDENTIAL or a SECURITY DECISION belongs to the person, so it is
 * namespaced even when its sibling is device-global. That is why the extension
 * inventory splits (bundle records device-global, grants/enable/uninstall
 * per-account — the same line Rust draws by keeping `extensions/<slug>/`
 * bundles device-wide while `storage.json` and the keychain entries are keyed
 * by account) and why the proxy splits (host/port device-global, username and
 * password per-account). Client certificates and extension storage are wholly
 * per-account: they are private key material and extension-held API tokens.
 */

export const LOCAL_NAMESPACE = '_local';

const AUTH_PERSIST_KEY = 'auth-storage';

/** Device-global marker: the stale pre-namespace keys have been purged. */
export const LEGACY_PURGE_KEY = 'apiboo-storage-ns-v1';
/**
 * Which GENERATION of the purge that marker records. A device that already ran
 * generation 1 (the original namespacing pass) still has the credential keys
 * that pass left behind — certificates, extension storage, extension grants,
 * proxy config — so the marker holds a generation rather than a bare flag, and
 * adding names to the lists below means bumping this. Extending the one purge
 * is the point: a second "purge the rest" mechanism beside it would be one
 * more thing to keep in sync, which is what this file exists to avoid.
 */
export const PURGE_GENERATION = '2';
/** Same, for the stale AppLocalData trees (purged by accountData.purgeLegacyDeviceTrees). */
export const LEGACY_FS_PURGE_KEY = 'apiboo-storage-ns-fs-v1';

/** Never namespaced, never dropped by the account teardown. */
const DEVICE_GLOBAL_KEYS = new Set([
  'apiboo-app-preferences',
  'apiboo-accounts',
  // The extension CODE inventory only: which bundles this device downloaded,
  // their version and on-disk path. Deliberately holds no grant, no enable
  // flag and no uninstall — those are per-account (see extensions/store.js).
  'apiboo-extensions-installed',
  // Proxy TRANSPORT only: enabled/type/host/port/noProxy. The credentials that
  // used to ride along in `apiboo-proxy-config` are per-account.
  'apiboo-proxy-transport',
  'apiboo-mirror-flat-layout-v1',
  'apiboo-sync-engine-v1-workspaces',
  'apiboo-sync-engine-v1-disabled',
  'apiboo-extensions-marketplace-enabled',
  LEGACY_PURGE_KEY,
  LEGACY_FS_PURGE_KEY,
]);

const DEVICE_GLOBAL_PREFIXES = ['apiboo.'];

/**
 * Stale keys from before namespacing existed. They are DELETED once, never
 * read: keeping a read path alive would keep the old names alive in the
 * codebase, which is exactly how another account's data gets picked up again.
 * Includes the names the two old hand-maintained logout lists enumerated —
 * several of which no writer ever produced.
 */
const LEGACY_LOCAL_WS_MANIFEST = 'apiboo-local-ws-manifest';
const LEGACY_LOCAL_SNAPSHOT_PREFIX = 'apiboo-local-snapshot-';

const LEGACY_EXACT_KEYS = [
  LEGACY_LOCAL_WS_MANIFEST,
  // Credentials that were device-global until they moved into the namespace:
  // client TLS certs (`apiboo-<ns>-certificates`), the extension store's
  // grants/enable/uninstall half (`apiboo-<ns>-extensions`, with the bundle
  // records re-homed to `apiboo-extensions-installed`), and the proxy config
  // whose username rode along with the transport
  // (`apiboo-<ns>-proxy-credentials` + `apiboo-proxy-transport`).
  'apiboo-certificates',
  'apiboo-extensions',
  'apiboo-proxy-config',
  'api-client-collections',
  'api-client-history',
  'api-client-environments',
  'api-client-selected-environment',
  'api-client-global-variables',
  'api-client-workspaces',
  'api-client-current-workspace',
  'api-client-teams-cache',
  // Dead names the previous logout lists removed (no writer ever created them).
  'workspaces',
  'teams',
  'collections',
  'environments',
  'history',
  'selectedEnvironment',
  'globalVariables',
  'currentWorkspace',
  'requestTabs',
  'environmentTabs',
  'activeTab',
  'workspace-storage',
  'team-storage',
  'app-storage',
];

const LEGACY_PREFIXES = [
  LEGACY_LOCAL_SNAPSHOT_PREFIX,
  'api-client-team-detail-',
  // Extension-held key/value data (API keys, tokens, connection configs) —
  // now `apiboo-<ns>-ext-storage:<extId>:<key>`.
  'apiboo-ext-storage:',
];

/** Un-namespaced sessionStorage keys, deleted by the same one-time purge. */
const LEGACY_SESSION_KEYS = ['apiboo-proxy-password'];

/**
 * Namespaced sessionStorage keys holding a secret that is deliberately never
 * written to disk (currently the proxy password). `apiboo-<ns>-<suffix>`.
 */
const SESSION_SECRET_SUFFIXES = ['proxy-password'];

let currentNamespace = null;
const namespaceListeners = new Set();

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (quota / disabled storage)
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore (no sessionStorage in some test envs)
  }
}

/**
 * All localStorage key names. `localStorage.length`/`key()` are the real API;
 * the jest global stub has neither, so fall back to Object.keys (its own
 * method names never match any prefix we care about).
 */
export function listStorageKeys() {
  try {
    if (typeof localStorage.length === 'number' && typeof localStorage.key === 'function') {
      const out = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) out.push(k);
      }
      return out;
    }
    return Object.keys(localStorage || {});
  } catch {
    return [];
  }
}

function readPersistedUserId() {
  try {
    const raw = safeGet(AUTH_PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? parsed ?? {};
    if (state?.isAuthenticated !== true) return null;
    const id = state?.user?.id;
    return id == null || String(id) === '' ? null : String(id);
  } catch {
    return null;
  }
}

function normalizeNamespace(userId) {
  return userId == null || String(userId) === '' ? LOCAL_NAMESPACE : String(userId);
}

/**
 * The namespace every account-owned key is written under. Resolved from the
 * zustand-persisted auth state on first use (persist rehydrates synchronously
 * at module load, so a cold start of a signed-in app already knows the user id
 * before `initializeAuth` finishes its /user round-trip) and thereafter kept
 * current by authStore via setStorageNamespace().
 */
export function getStorageNamespace() {
  if (currentNamespace == null) {
    currentNamespace = normalizeNamespace(readPersistedUserId());
    purgeLegacyStorageKeys();
  }
  return currentNamespace;
}

/** @param {string|number|null|undefined} userId — null/empty ⇒ signed-out `_local`. */
export function setStorageNamespace(userId) {
  const previous = currentNamespace;
  currentNamespace = normalizeNamespace(userId);
  purgeLegacyStorageKeys();
  // First resolution (previous == null) is not a change: nothing has read a
  // key under the old namespace yet, so nobody needs to re-read one.
  if (previous != null && previous !== currentNamespace) {
    for (const listener of [...namespaceListeners]) {
      try {
        listener(currentNamespace, previous);
      } catch (err) {
        console.warn('[storageNamespace] namespace listener failed:', err);
      }
    }
  }
  return currentNamespace;
}

/**
 * Subscribe to account switches. In-memory state loaded from a namespaced key
 * (the zustand `persist` stores) is stale the moment the namespace moves — it
 * still holds the OUTGOING account's values, which is the leak namespacing
 * exists to prevent. Listeners re-read their key under the new namespace; see
 * services/accountScopedPersist.js, the only intended consumer.
 *
 * @returns {() => void} unsubscribe
 */
export function onStorageNamespaceChange(listener) {
  namespaceListeners.add(listener);
  return () => namespaceListeners.delete(listener);
}

/** Test hook: forget the resolved namespace so the next read re-derives it. */
export function resetStorageNamespaceForTests() {
  currentNamespace = null;
}

/* ── Key builders — the ONLY place account key names are constructed ────── */

/** `apiboo-<ns>-<suffix>` (e.g. `local-snapshot-<id>`, `local-ws-manifest`). */
export function apibooKey(suffix, ns = getStorageNamespace()) {
  return `apiboo-${ns}-${suffix}`;
}

/** `api-client-<ns>-<suffix>` (e.g. `collections`, `history`, `teams-cache`). */
export function apiClientKey(suffix, ns = getStorageNamespace()) {
  return `api-client-${ns}-${suffix}`;
}

/**
 * The account's on-device DOCUMENTS: local workspace snapshots, their
 * manifest, the adoption ledger, cloud workspaces' Smart Mock schema trees.
 * Nothing here exists on a server, so a sign-out must not destroy it.
 */
function documentPrefix(ns = getStorageNamespace()) {
  return `apiboo-${ns}-`;
}

/**
 * The account's SESSION CACHES: mirrors of data the backend owns (collections,
 * environments, globals, the workspace list and pointer, teams). Losing these
 * costs a refetch, so a sign-out drops all of them.
 */
export function cachePrefix(ns = getStorageNamespace()) {
  return `api-client-${ns}-`;
}

export function namespacePrefixes(ns = getStorageNamespace()) {
  return [documentPrefix(ns), cachePrefix(ns)];
}

export function isDeviceGlobalKey(key) {
  if (DEVICE_GLOBAL_KEYS.has(key)) return true;
  return DEVICE_GLOBAL_PREFIXES.some((p) => key.startsWith(p));
}

/** Every existing localStorage key that belongs to `ns`. */
export function keysInNamespace(ns = getStorageNamespace()) {
  const prefixes = namespacePrefixes(ns);
  return listStorageKeys().filter(
    (k) => !isDeviceGlobalKey(k) && prefixes.some((p) => k.startsWith(p))
  );
}

/* ── Teardown ───────────────────────────────────────────────────────────── */

/**
 * THE logout teardown. Drops the outgoing account's session caches and returns
 * to `_local`. Replaces the two hand-maintained "clear these keys" lists
 * (authStore.logout + App.onLogoutReset) that had drifted into naming keys no
 * writer ever produced — one structural rule, nothing to keep in sync:
 *
 *   `api-client-<ns>-*`  session cache   → dropped (a refetch restores it)
 *   `apiboo-<ns>-*`      on-device docs  → kept
 *
 * A new cache key added anywhere in the app is torn down automatically because
 * it inherits the prefix; that is the whole point of namespacing over a list.
 *
 * Documents are deliberately KEPT: local workspace snapshots exist nowhere but
 * this device, so wiping them at sign-out would turn a routine sign-out into
 * permanent data loss. They are not a leak risk — the next account cannot
 * construct their key names, which is what the tests assert. Erasing an
 * account's device footprint outright is a separate, explicit action
 * (`account_data_purge` for the Rust-owned trees).
 *
 * Idempotent and safe from both logout paths (authStore.logout, and the
 * `auth:logout` event apiClient's 401 interceptor fires): once the namespace
 * is `_local` there is no account namespace left, so a second call is a no-op
 * and signed-out local-first data is never touched.
 *
 * @returns {string|null} the namespace that was cleared, or null.
 */
export function clearAccountNamespace() {
  const ns = getStorageNamespace();
  if (ns === LOCAL_NAMESPACE) return null;
  const prefix = cachePrefix(ns);
  for (const key of keysInNamespace(ns)) {
    if (key.startsWith(prefix)) safeRemove(key);
  }
  // Session-only secrets (never written to disk) still outlive a sign-out,
  // because the window doesn't close: the next account uses the same
  // sessionStorage. Drop the outgoing account's.
  for (const suffix of SESSION_SECRET_SUFFIXES) {
    safeSessionRemove(apibooKey(suffix, ns));
  }
  setStorageNamespace(null);
  return ns;
}

/* ── One-time purge of pre-namespace keys ───────────────────────────────── */

export function readPurgeMarker(key = LEGACY_PURGE_KEY) {
  const value = safeGet(key);
  return value == null || value === '' ? null : value;
}

export function writePurgeMarker(key = LEGACY_PURGE_KEY, value = PURGE_GENERATION) {
  safeSet(key, value);
}

/**
 * Merge two local-workspace manifests. On an id collision the NAMESPACED entry
 * wins: it was written by a build that already knows about namespaces, so it
 * describes the snapshot that is actually sitting in `_local` today, while the
 * legacy entry describes a pre-namespace copy that cannot have been touched
 * since the upgrade. Same rule on both halves (browser keys here, files in
 * services/accountData.js) so the two manifests cannot disagree about which
 * record is authoritative.
 *
 * @param {Array<object>} namespaced entries already under `apiboo-_local-*`
 * @param {Array<object>} legacy entries from the un-namespaced manifest
 */
export function mergeLocalWorkspaceManifests(namespaced, legacy) {
  const byId = new Map();
  for (const entry of Array.isArray(legacy) ? legacy : []) {
    if (entry?.id != null) byId.set(String(entry.id), entry);
  }
  for (const entry of Array.isArray(namespaced) ? namespaced : []) {
    if (entry?.id != null) byId.set(String(entry.id), entry);
  }
  return [...byId.values()];
}

function parseArray(raw) {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Write `value` at `key` and confirm it is really there (quota fails silently). */
function safeSetVerified(key, value) {
  safeSet(key, value);
  return safeGet(key) === value;
}

/**
 * Browser half of the one-time local-workspace rescue (the file half lives in
 * services/accountData.js). The pre-namespace snapshot keys hold local
 * workspace DOCUMENTS — collections, environments, variables that exist on no
 * server — so unlike every other legacy key they must not simply be deleted.
 * They are re-keyed into `_local`, the signed-out namespace, because that is
 * the bucket whose contents are OFFERED to whoever signs in next
 * (`adoptLocalNamespaceWorkspaces`). Deleting them answered the same ownership
 * question by destroying the evidence.
 *
 * @returns {{ complete: boolean, preserve: Set<string> }} `preserve` names the
 *   legacy keys whose migration did NOT succeed — the caller must not delete
 *   them. A half-move followed by a delete is the one outcome this whole
 *   function exists to prevent, so migrate-then-delete is the order and the
 *   delete is conditional on the move.
 */
function migrateLegacyLocalWorkspaceKeys() {
  const preserve = new Set();
  let complete = true;

  for (const key of listStorageKeys()) {
    if (!key.startsWith(LEGACY_LOCAL_SNAPSHOT_PREFIX)) continue;
    const id = key.slice(LEGACY_LOCAL_SNAPSHOT_PREFIX.length);
    const value = safeGet(key);
    if (!id || value == null) continue;

    const target = apibooKey(`local-snapshot-${id}`, LOCAL_NAMESPACE);
    const existing = safeGet(target);
    if (existing != null) {
      // Already namespaced. Identical bytes mean a previous run copied it and
      // died before dropping the source, so the source is redundant. Different
      // bytes mean two distinct snapshots share an id; the namespaced one wins
      // (see mergeLocalWorkspaceManifests) and the legacy one is KEPT rather
      // than overwritten or deleted — no branch here may lose a document.
      if (existing !== value) {
        console.warn(
          `[storageNamespace] ${target} already holds a different snapshot — keeping the pre-namespace ${key} instead of overwriting or deleting it`
        );
        preserve.add(key);
      }
      continue;
    }
    if (!safeSetVerified(target, value)) {
      console.warn(`[storageNamespace] could not re-key ${key} into ${target} — leaving it in place`);
      preserve.add(key);
      complete = false;
    }
  }

  const legacyManifestRaw = safeGet(LEGACY_LOCAL_WS_MANIFEST);
  if (legacyManifestRaw != null) {
    const target = apibooKey('local-ws-manifest', LOCAL_NAMESPACE);
    const legacyList = parseArray(legacyManifestRaw);
    const existingRaw = safeGet(target);
    const existingList = existingRaw == null ? [] : parseArray(existingRaw);
    if (existingRaw != null && existingList == null) {
      // Present but not a manifest: refuse to clobber it.
      console.warn(`[storageNamespace] ${target} is unparseable — keeping ${LEGACY_LOCAL_WS_MANIFEST}`);
      preserve.add(LEGACY_LOCAL_WS_MANIFEST);
      complete = false;
    } else if (legacyList == null) {
      // Nothing recoverable in the legacy manifest — the snapshots above are
      // the documents, and listLocalWorkspaceMetasResolved() re-derives a
      // manifest from the snapshot dirs anyway. Let it be deleted.
    } else {
      const merged = JSON.stringify(mergeLocalWorkspaceManifests(existingList ?? [], legacyList));
      if (!safeSetVerified(target, merged)) {
        console.warn(`[storageNamespace] could not merge ${LEGACY_LOCAL_WS_MANIFEST} into ${target}`);
        preserve.add(LEGACY_LOCAL_WS_MANIFEST);
        complete = false;
      }
    }
  }

  return { complete, preserve };
}

/**
 * Delete every un-namespaced account key, once per device PER GENERATION (see
 * PURGE_GENERATION). There is no dual-read on purpose: an un-namespaced key
 * has no owner, so a key that is only a CACHE is destroyed rather than
 * inherited. Device-global keys are untouched.
 *
 * The one exception is the local workspace documents, which exist nowhere else
 * — they are re-keyed into `_local` first (migrateLegacyLocalWorkspaceKeys),
 * and only the ones that made it are then dropped.
 */
export function purgeLegacyStorageKeys() {
  if (readPurgeMarker() === PURGE_GENERATION) return false;

  // MIGRATE BEFORE DELETING, and delete only what moved.
  const { complete, preserve } = migrateLegacyLocalWorkspaceKeys();

  let removed = false;
  for (const key of LEGACY_EXACT_KEYS) {
    if (preserve.has(key)) continue;
    if (safeGet(key) != null) {
      safeRemove(key);
      removed = true;
    }
  }
  for (const key of listStorageKeys()) {
    if (isDeviceGlobalKey(key) || preserve.has(key)) continue;
    if (LEGACY_PREFIXES.some((p) => key.startsWith(p))) {
      safeRemove(key);
      removed = true;
    }
  }
  for (const key of LEGACY_SESSION_KEYS) {
    safeSessionRemove(key);
  }

  // A failed re-key leaves the one-shot marker unwritten so the next launch
  // tries again — the alternative is a key stranded forever behind a marker
  // that says the migration is done.
  if (complete) writePurgeMarker();
  return removed;
}
